import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import { Vector3 } from 'three';
import { Core } from './Core';
import { OrbitController } from './OrbitController';
import { TickCallback } from './TickService';
import { Maths } from './Maths';
import { TweenService } from './TweenService';
import { CameraAngle } from '../data/CameraAngles';
import { BridgeActionResult, BridgeErrorStrings } from '../data/BridgeData';

export class CameraService {
	/**
	 * System Properties
	 * */
	core: Core;

	/**
	 * Objects
	 * */
	orbitController?: OrbitController;
	mainCamera: Camera;

	/**
	 * Camera Angles
	 */
	currentAngle?: CameraAngle;
	cameraAngles?: CameraAngle[];

	/**
	 * Defaults
	 */
	defaultFOV = 25;
	defaultNear = 0.1;
	defaultFar = 20;
	defaultCameraAngle: CameraAngle = {
		name: 'default',
		zoom: 0.5,
		x: 0,
		y: 0,
		z: 0,
		lookAt: new THREE.Vector3(0, 0.75, 0)
	};

	/**
	 * Processing vars
	 */
	lastLookAt = this.defaultCameraAngle.lookAt;
	fovMatched: boolean = false;

	/**
	 * Constructor
	 * */
	constructor(core: Core) {
		this.core = core;
		const defaultCamera = new THREE.PerspectiveCamera(this.defaultFOV, this.core.sizes.width / this.core.sizes.height, this.defaultNear, this.defaultFar);
		this.mainCamera = new Camera(this.defaultCameraAngle, defaultCamera);
	}

	/**
	 * Defines camera angles for use in the scene
	 * @param angles An array of CameraAngle objects to define various angles
	 */
	registerCameraAngles(angles: CameraAngle[]) {
		this.cameraAngles = angles;
	}

	/**
	 * Creates a perspective camera
	 * */
	createPerspectiveCamera(angle?: CameraAngle) {
		if (this.mainCamera) return console.error('Already have a camera');
		if (!angle) angle = (this.cameraAngles && this.cameraAngles[0]) || this.defaultCameraAngle;

		const threeCamera = new THREE.PerspectiveCamera(this.defaultFOV, this.core.sizes.width / this.core.sizes.height, this.defaultNear, this.defaultFar);
		threeCamera.zoom = angle.zoom;
		threeCamera.updateProjectionMatrix();

		const camera = new Camera(angle, threeCamera);
		camera.threeCamera.position.set(angle.x, angle.y, angle.z);
		camera?.threeCamera.lookAt(angle.lookAt?.x || 0, angle.lookAt?.y || 0.5, angle.lookAt?.z || 0);

		this.mainCamera = camera;
		return camera;
	}

	/**
	 * Switches to a new camera angle
	 * @param angleDetail A string name or CameraAngle object to switch to
	 * @param durationMS The duration the animation should take
	 * */
	switchCameraAngle(angleDetail: string | CameraAngle, durationMS: number = 1250): BridgeActionResult {
		let angle: CameraAngle | undefined;
		if (typeof (angleDetail) == "string") {
			angle = this.cameraAngles?.find(angle => angle.name == angleDetail);
		} else {
			angle = (angleDetail as CameraAngle);
		}

		if (!angle) return new BridgeActionResult(false, BridgeErrorStrings.no_camera);

		this.currentAngle = angle;
		this.moveMainCameraTo({ to: new THREE.Vector3(angle.x, angle.y, angle.z), durationMS: durationMS, lookAt: angle.lookAt, zoom: angle.zoom });
		return new BridgeActionResult(true);
	}

	/**
	 * Updates the camera by a position and rotation (and maybe zoom in the future, or refactor to split position, rotation and zoom)
	 * */
	changeCameraFOV(fov: number) {
		(this.mainCamera.threeCamera as THREE.PerspectiveCamera).fov = fov;
		(this.mainCamera.threeCamera as THREE.PerspectiveCamera).updateProjectionMatrix();
	}

	/**
	 * Sets up orbit handling
	 * */
	setupOrbitControls() {
		this.orbitController = new OrbitController(this.core.s('Camera').mainCamera.threeCamera, this.core.canvas);
		this.core.s('Tick').registerCallback(new TickCallback('OrbitController', () => {
			this.orbitController!.controls.update();
		}), false);

		// Set a max pan
		var minPan = new THREE.Vector3(-1, -1, -1);
		var maxPan = new THREE.Vector3(1, 1, 1);
		this.orbitController.controls.target = new Vector3(0, 0, 0);
		this.orbitController.controls.target.clamp(minPan, maxPan);

		// Set a max rotate
		this.orbitController.controls.minPolarAngle = this.mainCamera.angle.minPolarAngle || -Infinity;
		this.orbitController.controls.maxPolarAngle = this.mainCamera.angle.maxPolarAngle || Infinity;
		this.orbitController.controls.minAzimuthAngle = Number.isFinite(this.mainCamera.angle.minAzimuthAngle) ? this.mainCamera.angle.minAzimuthAngle : -Infinity;
		this.orbitController.controls.maxAzimuthAngle = Number.isFinite(this.mainCamera.angle.maxAzimuthAngle) ? this.mainCamera.angle.maxAzimuthAngle : -Infinity;
		this.orbitController.controls.minZoom = this.mainCamera.angle.minZoom || 0.1;
		this.orbitController.controls.maxZoom = this.mainCamera.angle.maxZoom || 5;

		// Call reset, which sets zoom properly
		this.orbitController.controls.reset();
	}

	/**
	 * Deletes and orbit controller
	 * */
	removeOrbitControls() {
		this.orbitController && this.orbitController.controls.dispose();
	}

	/**
	 * Moves the camera from one position to another
	 * @param to The ending position
	 * @param durationMS The duration the animation should take
	 * @param lookAt An optional Vector3 describing what the camera should look at as it moves
	 */
	moveMainCameraTo({ to, lookAt, zoom, durationMS = 2000 }: CameraMoveDefinition) {
		const sTween: TweenService = this.core.s('Tween');
		const from = new THREE.Vector3(this.mainCamera.threeCamera.position.x, this.mainCamera.threeCamera.position.y, this.mainCamera.threeCamera.position.z);
		this.mainCamera.threeCamera.position.set(from.x, from.y, from.z);

		// Update the position and lookAt
		if (to) {
			sTween.createTween({
				name: 'main_camera_move',
				targetObject: this.mainCamera.threeCamera.position,
				modifiedPropertyObject: to,
				durationMS: durationMS,
				easing: TWEEN.Easing.Cubic.InOut,
				onUpdate: (_: any, elapsedTime: any) => {
					if (lookAt) {
						const easedT = Maths.easeInOutQuad(elapsedTime);
						const pointBetween = new THREE.Vector3().lerpVectors(this.lastLookAt, lookAt, easedT);

						this.mainCamera.threeCamera.lookAt(pointBetween.x, pointBetween.y, pointBetween.z);
					}
				},
				onComplete: () => {
					if (lookAt) {
						this.mainCamera.threeCamera.lookAt(lookAt.x, lookAt.y, lookAt.z);
						this.lastLookAt = lookAt;
					}
				}
			});
		}

		// Also update the zoom if required
		if (zoom) {
			sTween.createTween({
				name: 'main_camera_move_zoom',
				targetObject: this.mainCamera.threeCamera,
				modifiedPropertyObject: { zoom: zoom },
				durationMS: durationMS,
				easing: TWEEN.Easing.Cubic.InOut,
				onUpdate: () => {
					this.mainCamera.threeCamera.updateProjectionMatrix();
				},
			});
		}
	}

	/**
	 * Moves the camera in and out depending on whether it can fully see the provided group
	 */
	adjustCameraPositionToViewGroup(/*group: THREE.Group*/) {
		return "This doesn't quite work well enough, and won't anyway the character's box doesn't change when you apply an animation via animationmixer to it (the skeleton and the model moves, but it's bounding box is considered where it originally was)";
		// PLUS, we really could do this with zoom instead of moving the camera!
		/*const maths = new Maths();
		const temporaryCamera = this.mainCamera.threeCamera.clone();
		const characterBoundingBox = new THREE.Box3().setFromObject(group);
		//characterBoundingBox.expandByScalar(0.01);
		const points = [
			new THREE.Vector3(characterBoundingBox.min.x, characterBoundingBox.min.y, characterBoundingBox.min.z),
			new THREE.Vector3(characterBoundingBox.min.x, characterBoundingBox.min.y, characterBoundingBox.max.z),
			new THREE.Vector3(characterBoundingBox.min.x, characterBoundingBox.max.y, characterBoundingBox.min.z),
			new THREE.Vector3(characterBoundingBox.min.x, characterBoundingBox.max.y, characterBoundingBox.max.z),
			new THREE.Vector3(characterBoundingBox.max.x, characterBoundingBox.min.y, characterBoundingBox.min.z),
			new THREE.Vector3(characterBoundingBox.max.x, characterBoundingBox.min.y, characterBoundingBox.max.z),
			new THREE.Vector3(characterBoundingBox.max.x, characterBoundingBox.max.y, characterBoundingBox.min.z),
			new THREE.Vector3(characterBoundingBox.max.x, characterBoundingBox.max.y, characterBoundingBox.max.z),
		];

		const frustum = this.createFrustumComponents();
		let initiallyFullyVisible = this.checkFrustrumForPoints(frustum, points);
		const newCameraPosition = new THREE.Vector3();
		console.log("Was initially visible?", initiallyFullyVisible);

		let idiotCheck = 0;
		let isFullyVisible;

		do {
			idiotCheck += 1;

			temporaryCamera.updateMatrixWorld(); // Ensure camera matrices are up to date
			temporaryCamera.matrixWorldInverse.copy(temporaryCamera.matrixWorld).invert();

			const lookAtPosition = new THREE.Vector3(this.lastLookAt.x, this.lastLookAt.y, this.lastLookAt.z);
			const temporaryCameraPosition = new THREE.Vector3(temporaryCamera.position.x, temporaryCamera.position.y, temporaryCamera.position.z);

			if (initiallyFullyVisible) {
				const zoomPoint = maths.extendLine3D(lookAtPosition, temporaryCameraPosition, -1);
				temporaryCamera.position.set(zoomPoint.x, zoomPoint.y, zoomPoint.z);
				newCameraPosition.set(zoomPoint.x, zoomPoint.y, zoomPoint.z);
			} else {
				const zoomPoint = maths.extendLine3D(lookAtPosition, temporaryCameraPosition, 1);
				temporaryCamera.position.set(zoomPoint.x, zoomPoint.y, zoomPoint.z);
				newCameraPosition.set(zoomPoint.x, zoomPoint.y, zoomPoint.z);
			}

			const frustum = this.createFrustumComponents(temporaryCamera);
			isFullyVisible = this.checkFrustrumForPoints(frustum, points);

			console.log("Group is fully visible", isFullyVisible, temporaryCamera.position.x, temporaryCamera.position.y, temporaryCamera.position.z);
			console.log((initiallyFullyVisible && !isFullyVisible));
		}
		while (
			(
				(!initiallyFullyVisible && !isFullyVisible)
			)
			&& idiotCheck < 100
		);

		const newPointDistance = maths.distanceBetweenPoints(newCameraPosition, this.mainCamera.threeCamera.position);
		if (newPointDistance > 3) {
			this.moveMainCameraTo({ to: newCameraPosition, durationMS: 1500, lookAt: this.lastLookAt });
			//this.mainCamera.threeCamera.position.set(newCameraPosition.x, newCameraPosition.y, newCameraPosition.z);
		}

		if (idiotCheck == 100) throw new Error("Infinite Loop in setPerfectZoomForGroup");*/
	}

	/**
	 * Creates a frustum
	 * @param camera 
	 * @returns 
	 */
	/*createFrustumComponents(camera: THREE.Camera = this.mainCamera.threeCamera) {
		const frustum = new THREE.Frustum();
		const cameraViewProjectionMatrix = new THREE.Matrix4();
		cameraViewProjectionMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
		frustum.setFromProjectionMatrix(cameraViewProjectionMatrix);
		return frustum;
	}

	checkFrustrumForPoints(frustum: any, points: any) {
		let isFullyVisible = true;
		for (let i = 0; i < points.length; i++) {
			if (!frustum.containsPoint(points[i])) {
				isFullyVisible = false;
				break;
			}
		}
		return isFullyVisible;
	}*/

	/**
	 * Adjust the camera to match the character's bounding box
	 * @param boundingBox A THREE.Box describing the object you wish to fit the camera FOV to.
	 * @param zoomOut A direction.  True will zoom out, and false will zoom in.
	 */
	/*matchCameraFOVToBoundingBox(boundingBox: THREE.Box3, zoomOut: boolean = true) {
		this.fovMatched = false;
		this.continueMatchCameraFOVToBoundingBox(boundingBox, zoomOut);
	}*/

	/**
	 * Performs the zooming
	 * @param boundingBox A THREE.Box describing the object you wish to fit the camera FOV to.
	 * @param zoomOut A direction.  True will zoom out, and false will zoom in.
	 */
	/*continueMatchCameraFOVToBoundingBox(boundingBox: THREE.Box3, zoomOut: boolean = true) {
		const frustum = new THREE.Frustum();
		const cameraViewProjectionMatrix = new THREE.Matrix4();

		this.mainCamera.threeCamera.updateMatrixWorld(); // Ensure camera matrices are up to date
		this.mainCamera.threeCamera.matrixWorldInverse.copy(this.mainCamera.threeCamera.matrixWorld).invert();
		cameraViewProjectionMatrix.multiplyMatrices(this.mainCamera.threeCamera.projectionMatrix, this.mainCamera.threeCamera.matrixWorldInverse);
		frustum.setFromProjectionMatrix(cameraViewProjectionMatrix);

		const points = [
			new THREE.Vector3(boundingBox.min.x, boundingBox.min.y, boundingBox.min.z),
			new THREE.Vector3(boundingBox.min.x, boundingBox.min.y, boundingBox.max.z),
			new THREE.Vector3(boundingBox.min.x, boundingBox.max.y, boundingBox.min.z),
			new THREE.Vector3(boundingBox.min.x, boundingBox.max.y, boundingBox.max.z),
			new THREE.Vector3(boundingBox.max.x, boundingBox.min.y, boundingBox.min.z),
			new THREE.Vector3(boundingBox.max.x, boundingBox.min.y, boundingBox.max.z),
			new THREE.Vector3(boundingBox.max.x, boundingBox.max.y, boundingBox.min.z),
			new THREE.Vector3(boundingBox.max.x, boundingBox.max.y, boundingBox.max.z),
		];

		let isFullyVisible = true;

		for (let i = 0; i < points.length; i++) {
			if (!frustum.containsPoint(points[i])) {
				isFullyVisible = false;
				break;
			}
		}

		if (zoomOut && !isFullyVisible) {
			(this.mainCamera.threeCamera as THREE.PerspectiveCamera).fov = (this.mainCamera.threeCamera as THREE.PerspectiveCamera).fov + 1;
			(this.mainCamera.threeCamera as THREE.PerspectiveCamera).updateProjectionMatrix();

			return setTimeout(() => {
				this.continueMatchCameraFOVToBoundingBox(boundingBox, zoomOut);
				console.error("Camera Zoom event occurred");
			}, 100);
		}

		if (!zoomOut && isFullyVisible && !this.fovMatched) {
			(this.mainCamera.threeCamera as THREE.PerspectiveCamera).fov = (this.mainCamera.threeCamera as THREE.PerspectiveCamera).fov - 1;
			(this.mainCamera.threeCamera as THREE.PerspectiveCamera).updateProjectionMatrix();

			setTimeout(() => {
				console.error("Camera Zoom event occurred");
				this.continueMatchCameraFOVToBoundingBox(boundingBox, zoomOut);
			}, 15);
		} else if (!isFullyVisible && !zoomOut && !this.fovMatched) {
			this.fovMatched = true;
		}
	}*/

	/**
	 * The window was resized
	 * */
	resizeEvent() {
		if (this.mainCamera.threeCamera instanceof THREE.PerspectiveCamera) {
			this.mainCamera.threeCamera.aspect = this.core.sizes.width / this.core.sizes.height;
			this.mainCamera.threeCamera.updateProjectionMatrix();
		}
	}

	/**
	 * Console logs the main camera's object
	 */
	debugLogCameraPosition() {
		console.log(this.mainCamera.threeCamera);
	}
}

export class Camera {
	angle: any;
	threeCamera: THREE.PerspectiveCamera;

	constructor(angle: CameraAngle, threeCamera: THREE.PerspectiveCamera) {
		this.angle = angle;
		this.threeCamera = threeCamera;
	}
}

export interface CameraMoveDefinition {
	to?: THREE.Vector3;
	durationMS?: number;
	lookAt?: THREE.Vector3;
	zoom?: number;
}
